import * as React from 'react'
import clx from 'classnames'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import { DropdownIcon } from '@/app/features/icons/Dropdown'

type Language = { code: string; name?: string }

type Props = {
  collapsed?: boolean
  languages: Language[]
  activeCode?: string
  onSelect: (code: string) => void
}

const SUPPORTED_CODES = ['pl', 'en', 'es', 'ua']

const Flag = ({ code }: { code: string }) => (
  <img
    src={`/assets/img/flags/${code.toUpperCase()}.png`}
    alt={code}
    className="tw-h-3 tw-w-5 tw-shrink-0 tw-object-cover"
  />
)

export const SidebarLanguageSwitcher = ({
  collapsed,
  languages,
  activeCode,
  onSelect,
}: Props) => {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  useClickOutside({ ref, handler: () => setOpen(false) })

  const options = languages.filter((l) => SUPPORTED_CODES.includes(l.code))
  const active = options.find((l) => l.code === activeCode) ?? options[0]

  if (!active) return null

  return (
    <div ref={ref} className="tw-relative tw-px-2 tw-py-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={active.name ?? active.code.toUpperCase()}
        className={clx(
          'tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded tw-p-1.5 tw-text-content-base tw-transition-colors hover:tw-bg-base-200',
          collapsed ? 'tw-justify-center' : 'md:tw-justify-start',
        )}
      >
        <Flag code={active.code} />
        {!collapsed && (
          <>
            <span className="tw-hidden tw-truncate tw-text-sm md:tw-block">
              {active.name ?? active.code.toUpperCase()}
            </span>
            <DropdownIcon
              className={clx(
                'tw-ml-auto tw-hidden tw-transition-transform md:tw-block',
                open && 'tw-rotate-180',
              )}
            />
          </>
        )}
      </button>

      {open && (
        <ul
          className="tw-absolute tw-bottom-full tw-left-2 tw-z-[300] tw-mb-1 tw-flex tw-w-max tw-min-w-[150px] tw-flex-col tw-gap-0.5 tw-rounded-md tw-border tw-border-base-300 tw-bg-base-100 tw-p-1 tw-shadow-multi"
        >
          {options.map((lang) => (
            <li key={lang.code}>
              <button
                type="button"
                onClick={() => {
                  onSelect(lang.code)
                  setOpen(false)
                }}
                className={clx(
                  'tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded tw-p-1.5 tw-text-sm tw-text-content-base tw-transition-colors hover:tw-bg-base-200',
                  lang.code === active.code
                    ? 'tw-opacity-100'
                    : 'tw-opacity-60 hover:tw-opacity-100',
                )}
              >
                <Flag code={lang.code} />
                <span className="tw-whitespace-nowrap">
                  {lang.name ?? lang.code.toUpperCase()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
